/**
 * Difficulty.
 *
 * Two dials, and deliberately only two. A time budget decides how far ahead it looks;
 * evaluation noise decides how reliably it picks the move it found. Nothing weakens the
 * bot by feeding it wrong rules or by making it skip moves at random, because both of
 * those produce a bot that is bad in ways a person cannot learn anything from.
 *
 * Noise is added at the leaves and derived from the position's hash, so the same position
 * is always worth the same to a given search. A bot whose evaluation wobbled between
 * visits would contradict itself inside one search and change its mind for no reason
 * between them.
 */

export type LevelId = 'gentle' | 'steady' | 'sharp' | 'relentless';

export interface Level {
  readonly id: LevelId;
  readonly label: string;
  /** One line, written for someone choosing an opponent rather than reading a spec. */
  readonly blurb: string;
  readonly budgetMs: number;
  readonly maxDepth: number;
  /** Centipawns of evaluation noise. */
  readonly noise: number;
}

export const LEVELS: readonly Level[] = [
  {
    id: 'gentle',
    label: 'Gentle',
    blurb: 'Barely looks ahead, and misjudges what it sees. A place to start.',
    budgetMs: 80,
    maxDepth: 2,
    noise: 110,
  },
  {
    id: 'steady',
    label: 'Steady',
    blurb: 'Takes what you hang and keeps its own pieces defended.',
    budgetMs: 350,
    maxDepth: 8,
    noise: 30,
  },
  {
    id: 'sharp',
    label: 'Sharp',
    blurb: 'Builds bows, and sees yours coming a move before they fire.',
    budgetMs: 1200,
    maxDepth: 16,
    noise: 0,
  },
  {
    id: 'relentless',
    label: 'Relentless',
    blurb: 'Thinks for a few seconds a move. Expect to have to work for it.',
    budgetMs: 3500,
    maxDepth: 24,
    noise: 0,
  },
];

export const DEFAULT_LEVEL: LevelId = 'steady';

export const levelById = (id: LevelId): Level =>
  LEVELS.find((level) => level.id === id) ?? LEVELS[1];
